/**
 * `RequireRole` — shared role-based guard for admin route trees.
 *
 * Renders its contents (either the route `<Outlet />` or the provided
 * `children`) when the authenticated user has at least one of the
 * supplied `roles`. Otherwise redirects to `fallbackPath` (default
 * `/403`) with `state.from` preserved for post-fix return flows.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAppSelector } from '@dfq/shared/hooks/useStore';

/**
 * Admin role keys. Mirrors the platform's real `UserRole` enum
 * (`platform/app/Enums/UserRole.php`) for staff accounts:
 *   superuser | admin | manager | csr | prepress | fulfillment
 * String fallback lets downstream consumers plumb future roles without
 * a breaking type change.
 */
export type AdminRole =
  | 'superuser'
  | 'admin'
  | 'manager'
  | 'csr'
  | 'prepress'
  | 'fulfillment'
  | (string & {});

export interface RequireRoleProps {
  /** Allowed roles — the active user must have at least one to pass. */
  roles: AdminRole[];
  /** Where to redirect an unauthorized user. Defaults to `/403`. */
  fallbackPath?: string;
  /**
   * Optional children. When omitted the component renders `<Outlet />`
   * so it can be dropped into a `createBrowserRouter` route as a layout
   * guard. Pre-existing callers that wrap a concrete view
   * (`<RequireRole><AdminLayout /></RequireRole>`) continue to work.
   */
  children?: ReactNode;
}

export function RequireRole({
  roles,
  fallbackPath = '/403',
  children,
}: RequireRoleProps) {
  const userRoles = useAppSelector((state) => state.auth.roles);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const location = useLocation();

  const hasRole = isAuthenticated && userRoles.some((r) => roles.includes(r));

  if (!hasRole) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return children === undefined ? <Outlet /> : <>{children}</>;
}

export default RequireRole;
